import { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { getAllCategories } from '../data/categories';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Composant de filtre multi-catégories pour la recherche
 * @param {Array} selectedCategories - Catégories sélectionnées
 * @param {Function} onChange - Callback quand les sélections changent
 */
const MultiCategoryFilter = ({ selectedCategories = [], onChange }) => {
  const [open, setOpen] = useState(false);
  const allCategories = getAllCategories();

  const handleCategoryToggle = (categoryId) => {
    if (selectedCategories.includes(categoryId)) {
      onChange(selectedCategories.filter(id => id !== categoryId));
    } else {
      onChange([...selectedCategories, categoryId]);
    }
  };

  const clearAll = () => {
    onChange([]);
  };

  const selectedCategoriesData = allCategories.filter(cat => 
    selectedCategories.includes(cat.id)
  );

  return (
    <div className="space-y-3">
      {/* Bouton pour ouvrir/fermer le filtre */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button 
          onClick={() => setOpen(!open)}
          variant="outline" 
          className="gap-2"
        >
          <Filter className="w-4 h-4" />
          Filtrer par catégories
          {selectedCategories.length > 0 && (
            <Badge variant="secondary" className="ml-1 px-1.5">
              {selectedCategories.length}
            </Badge>
          )}
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>

        {/* Afficher les catégories sélectionnées */}
        {selectedCategoriesData.map((category) => (
          <Badge
            key={category.id}
            variant="secondary"
            className="px-3 py-1.5 flex items-center gap-2"
            style={{
              backgroundColor: category.color + '20',
              color: category.color
            }}
          >
            {category.name}
            <button
              onClick={() => handleCategoryToggle(category.id)}
              className="hover:bg-black/10 rounded-full p-0.5"
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>

      {/* Panel de sélection */}
      {open && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Sélectionner des catégories</CardTitle>
              {selectedCategories.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAll}
                  className="h-auto p-1 text-xs"
                >
                  Tout effacer
                </Button>
              )}
            </div>
            <CardDescription>
              Cochez les catégories à filtrer
            </CardDescription>
          </CardHeader>
          
          <CardContent className="max-h-96 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {allCategories.map((category) => {
                const isSelected = selectedCategories.includes(category.id);
                
                return (
                  <div
                    key={category.id}
                    className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md cursor-pointer border border-gray-200"
                    onClick={() => handleCategoryToggle(category.id)}
                  >
                    <Checkbox
                      id={`filter-${category.id}`}
                      checked={isSelected}
                      onCheckedChange={() => handleCategoryToggle(category.id)}
                    />
                    <Label
                      htmlFor={`filter-${category.id}`}
                      className="flex items-center gap-2 cursor-pointer flex-1"
                    >
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: category.color }}
                      />
                      <span className="text-sm">{category.name}</span>
                    </Label>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MultiCategoryFilter;
